import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import RichTextWrapper from './RichTextStyles'
import { RichTextBlockNode, RichTextContentNode, RichTextTableCellNode } from '../../types/types'
import { AppContext } from '../../utils/appProvider'
import { PRODUCTION_ENVIRONMENT } from '../../utils/enums'
import { getLinkAttributes } from '../../utils/helper'

type RichTextProps = {
	data: RichTextBlockNode[]
}

const getHtmlTag = (block: RichTextBlockNode): string | null => {
	if (block.type === 'heading') {
		// Supported heading levels are 1, 2, 3
		const level = Math.max(1, Math.min(block.props.level || 1, 3))
		return `h${level}`
	}

	if (block.type === 'paragraph') {
		return 'p'
	}

	return null
}
// sanitize function to prevent XSS attacks
const sanitize = (input: string): string => {
	return input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;')
}

const renderContent = (content: RichTextContentNode, productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string): string => {
	if (content.type === 'link' && content.href && content.content) {
		const safeHref = sanitize(content.href)
		const inner = content.content.map((itemContent) => renderContent(itemContent, productionEnvironment, locale)).join('')
		const { rel, target } = getLinkAttributes(safeHref, productionEnvironment, locale)
		const linkAttrs = [`href="${safeHref}"`]
		if (target) {
			linkAttrs.push(`target="${target}"`)
		}
		if (rel) {
			linkAttrs.push(`rel="${rel}"`)
		}
		return `<a ${linkAttrs.join(' ')}>${inner}</a>`
	}

	if (content.type === 'text') {
		// sanitize text to prevent XSS attacks
		let text = sanitize(content.text || '')
		const styles = content.styles || {}

		const wrappers: [string, string][] = []
		if (styles.bold) wrappers.push(['<strong>', '</strong>'])
		if (styles.italic) wrappers.push(['<em>', '</em>'])
		if (styles.underline) wrappers.push(['<u>', '</u>'])
		if (styles.strike) wrappers.push(['<s>', '</s>'])

		const dataAttrs: string[] = []
		if (styles.textColor && styles.textColor !== 'default') {
			const safeColor = sanitize(styles.textColor)
			dataAttrs.push(`data-text-color="${safeColor}"`)
		}
		if (styles.backgroundColor && styles.backgroundColor !== 'default') {
			const safeBg = sanitize(styles.backgroundColor)
			dataAttrs.push(`data-background-color="${safeBg}"`)
		}

		if (dataAttrs.length > 0) {
			text = `<span ${dataAttrs.join(' ')}>${text}</span>`
		}

		text = wrappers.reduceRight((acc, [open, close]) => `${open}${acc}${close}`, text)
		return text
	}
	return ''
}

const renderTableCell = (cell: RichTextTableCellNode, productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string, tag: 'td' | 'th'): string => {
	const attrs: string[] = []
	const alignment = cell.props?.textAlignment

	if (alignment && ['left', 'center', 'right'].includes(alignment)) {
		attrs.push(`data-text-alignment="${sanitize(alignment)}"`)
	}

	if (cell.props?.backgroundColor && cell.props.backgroundColor !== 'default') {
		attrs.push(`data-background-color="${sanitize(cell.props.backgroundColor)}"`)
	}

	if (cell.props?.textColor && cell.props.textColor !== 'default') {
		attrs.push(`data-text-color="${sanitize(cell.props.textColor)}"`)
	}

	if (cell.props?.colspan > 1) {
		attrs.push(`colspan="${cell.props.colspan}"`)
	}

	if (cell.props?.rowspan > 1) {
		attrs.push(`rowspan="${cell.props.rowspan}"`)
	}

	const innerHtml = cell.content.map((itemContent) => renderContent(itemContent, productionEnvironment, locale)).join('')
	const attrsString = attrs.length > 0 ? ` ${attrs.join(' ')}` : ''

	return `<${tag}${attrsString}>${innerHtml}</${tag}>`
}

const renderTable = (block: RichTextBlockNode & { type: 'table' }, productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string): string => {
	const { rows } = block.content

	if (!rows.length) {
		return '<div class="rich-text-table-scroll" style="--column-count:0"><div class="rich-text-table-wrapper"><table></table></div></div>'
	}

	const [headerRow, ...bodyRows] = rows
	const columnCount = headerRow.cells.length
	const colgroup = `<colgroup>${'<col />'.repeat(columnCount)}</colgroup>`
	const headerCells = headerRow.cells.map((cell) => renderTableCell(cell, productionEnvironment, locale, 'th')).join('')
	const bodyHtml = bodyRows.map((row) => `<tr>${row.cells.map((cell) => renderTableCell(cell, productionEnvironment, locale, 'td')).join('')}</tr>`).join('')

	return `<div class="rich-text-table-scroll" style="--column-count:${columnCount}"><div class="rich-text-table-wrapper"><table>${colgroup}<thead><tr>${headerCells}</tr></thead><tbody>${bodyHtml}</tbody></table></div></div>`
}

const parseToHtml = (blocks: RichTextBlockNode[], productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string): string => {
	const { html } = blocks.reduce<{ html: string; skip: number }>(
		(acc, block, index) => {
			// Skip blocks that were already processed as part of a list
			if (acc.skip > 0) {
				return { ...acc, skip: acc.skip - 1 }
			}

			// Handle bullet list items
			if (block.type === 'bulletListItem') {
				const remainingBlocks = blocks.slice(index)
				const endIndex = remainingBlocks.findIndex((b) => b.type !== 'bulletListItem')
				const listBlocks = endIndex === -1 ? remainingBlocks : remainingBlocks.slice(0, endIndex)

				const listItems = listBlocks.map((item) => {
					if (item.type !== 'bulletListItem') {
						return ''
					}

					const innerHtml = item.content.map((itemContent) => renderContent(itemContent, productionEnvironment, locale)).join('')
					return `<li>${innerHtml}</li>`
				})

				return {
					html: `${acc.html}<ul>${listItems.join('')}</ul>`,
					skip: listBlocks.length - 1
				}
			}

			// Handle numbered list items
			if (block.type === 'numberedListItem') {
				const remainingBlocks = blocks.slice(index)
				const endIndex = remainingBlocks.findIndex((b) => b.type !== 'numberedListItem')
				const listBlocks = endIndex === -1 ? remainingBlocks : remainingBlocks.slice(0, endIndex)

				const listItems = listBlocks.map((item) => {
					if (item.type !== 'numberedListItem') {
						return ''
					}

					const innerHtml = item.content.map((itemContent) => renderContent(itemContent, productionEnvironment, locale)).join('')
					return `<li>${innerHtml}</li>`
				})

				return {
					html: `${acc.html}<ol>${listItems.join('')}</ol>`,
					skip: listBlocks.length - 1
				}
			}

			if (block.type === 'table') {
				return { html: `${acc.html}${renderTable(block, productionEnvironment, locale)}`, skip: 0 }
			}

			// Handle other block types (headings, paragraphs)
			const tag = getHtmlTag(block)
			if (!tag) {
				return { html: `${acc.html}<span>Unsupported block: ${block.type}</span>`, skip: 0 }
			}

			if (block.type === 'paragraph' && block.content.length === 0) {
				return { html: `${acc.html}<p></p>`, skip: 0 }
			}

			const alignment = block.props?.textAlignment
			const alignmentAttr = alignment && ['left', 'center', 'right'].includes(alignment) ? ` data-text-alignment="${sanitize(alignment)}"` : ''
			const innerHtml = block.content.map((itemContent) => renderContent(itemContent, productionEnvironment, locale)).join('')

			return { html: `${acc.html}<${tag}${alignmentAttr}>${innerHtml}</${tag}>`, skip: 0 }
		},
		{ html: '', skip: 0 }
	)

	return html
}

const RichText: React.FC<RichTextProps> = ({ data }) => {
	const { productionEnvironment } = useContext(AppContext)
	const { locale } = useIntl()

	const htmlResult = parseToHtml(data, productionEnvironment, locale)

	return <RichTextWrapper className='blocknote-content' dangerouslySetInnerHTML={{ __html: htmlResult }} />
}

export default RichText
